import { Container, Section } from '@resolute/ui';
import { calculateEmi, formatCurrency, formatINRCompact } from '@resolute/utils';
import { useMemo, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';


import { CalcLayout, ResultStat, SliderField, ToolDisclaimer } from './components/calc-ui';

import { Seo } from '@/lib/seo';


export default function EmiCalculatorPage() {
  const [price, setPrice] = useState(12_500_000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(8.5);
  const [tenureYears, setTenureYears] = useState(20);

  const { loan, emi, totalInterest, totalPayable } = useMemo(() => {
    const loanAmount = Math.round(price * (1 - downPct / 100));
    const r = calculateEmi({ principal: loanAmount, annualRatePct: rate, tenureMonths: tenureYears * 12 });
    return { loan: loanAmount, emi: r.emi, totalInterest: r.totalInterest, totalPayable: r.totalPayable };
  }, [price, downPct, rate, tenureYears]);

  const pie = [
    { name: 'Principal', value: loan },
    { name: 'Interest', value: totalInterest },
  ];
  const COLORS = ['hsl(var(--primary))', 'hsl(var(--muted-foreground))'];

  return (
    <Section>
      <Seo
        title="Home Loan EMI Calculator"
        description="Estimate your monthly home-loan EMI, total interest and payment breakdown. Free EMI calculator for property buyers in India."
        path="/calculators/emi"
      />
      <Container className="max-w-5xl">
        <header className="mb-8">
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            Home Loan EMI Calculator
          </h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Work out your equated monthly instalment, total interest, and how much of your loan goes
            to interest over the tenure.
          </p>
        </header>

        <CalcLayout
          controls={
            <>
              <SliderField
                label="Property price"
                value={price}
                onChange={setPrice}
                min={2_500_000}
                max={100_000_000}
                step={100_000}
                display={formatINRCompact}
              />
              <SliderField
                label="Down payment"
                value={downPct}
                onChange={setDownPct}
                min={0}
                max={80}
                step={1}
                display={(v) => `${v}%  ·  ${formatINRCompact((price * v) / 100)}`}
              />
              <SliderField
                label="Interest rate (p.a.)"
                value={rate}
                onChange={setRate}
                min={5}
                max={15}
                step={0.05}
                display={(v) => `${v.toFixed(2)}%`}
              />
              <SliderField
                label="Loan tenure"
                value={tenureYears}
                onChange={setTenureYears}
                min={1}
                max={30}
                step={1}
                display={(v) => `${v} ${v === 1 ? 'year' : 'years'}`}
              />
            </>
          }
          results={
            <>
              <div>
                <ResultStat label="Monthly EMI" value={formatCurrency(emi)} emphasis />
              </div>

              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2}>
                      {pie.map((entry, i) => (
                        <Cell key={entry.name} fill={COLORS[i]} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v: number, n) => [formatCurrency(v), n as string]}
                      contentStyle={{
                        background: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <dl className="grid grid-cols-2 gap-4">
                <ResultStat label="Loan amount" value={formatINRCompact(loan)} tone="foreground" />
                <ResultStat label="Total interest" value={formatINRCompact(totalInterest)} tone="foreground" />
                <ResultStat label="Total payable" value={formatINRCompact(totalPayable)} tone="foreground" />
                <ResultStat
                  label="Interest share"
                  value={`${totalPayable > 0 ? Math.round((totalInterest / totalPayable) * 100) : 0}%`}
                  tone="foreground"
                />
              </dl>
              <ToolDisclaimer />
            </>
          }
        />
      </Container>
    </Section>
  );
}
